import { hideOwnFlags } from '../meta';
import { savePrefs, usePrefs, type Prefs } from '../prefs';
import { TileFlags } from './MediaFlags';

/**
 * Labels op posters (Instellingen → Metadata en artwork): kant-en-klare stijlen of zelf kiezen,
 * met een voorbeeld dat meteen meeverandert. Geldt voor dit profiel, op elk apparaat.
 */

type P = Pick<Prefs, 'posterLabels' | 'posterLabelsPos' | 'posterLabelSize' | 'posterScore'>;

const PRESETS: { id: string; label: string; hint: string; v: P }[] = [
  { id: 'min', label: 'Minimaal', hint: 'Alleen beeld: 4K, HDR, DV', v: { posterLabels: 'basic', posterLabelsPos: 'tl', posterLabelSize: 'small', posterScore: false } },
  { id: 'cinema', label: 'Bioscoop', hint: 'Beeld, geluid en score', v: { posterLabels: 'full', posterLabelsPos: 'tl', posterLabelSize: 'normal', posterScore: true } },
  { id: 'feature', label: 'Uitgelicht', hint: 'Groot, onderaan, met score', v: { posterLabels: 'full', posterLabelsPos: 'bl', posterLabelSize: 'large', posterScore: true } },
  { id: 'off', label: 'Uit', hint: 'Geen labels', v: { posterLabels: 'off', posterLabelsPos: 'skin', posterLabelSize: 'normal', posterScore: false } },
];

const SAMPLE: { label: string; kind: 'res' | 'hdr' | 'audio' }[] = [
  { label: '4K', kind: 'res' },
  { label: 'DV', kind: 'hdr' },
  { label: 'Atmos', kind: 'audio' },
];

export function PosterLabelSettings() {
  const p = usePrefs();
  const same = (v: P) => v.posterLabels === p.posterLabels && v.posterLabelsPos === p.posterLabelsPos && v.posterLabelSize === p.posterLabelSize && v.posterScore === p.posterScore;
  const custom = !PRESETS.some((x) => same(x.v));

  return (
    <section className="settings-block" aria-label="Labels op posters">
      <h2>Labels op posters</h2>
      <p className="subtle hint">Kwaliteit (4K, HDR, Dolby Vision), geluid (Atmos, DTS) en de score van Plex-titels. Geldt voor dit profiel op elk apparaat. Het label over de bibliotheek of een aanvraag staat altijd rechtsboven. Kwaliteit en score krijgen elk een eigen hoek; kies je een bezette hoek, dan schuift tumedia ze vanzelf naar een vrije.</p>
      {hideOwnFlags() && <p className="hint">SpatialPosters zet nu zelf labels op de posters; daarom worden deze labels niet getoond.</p>}
      <div className="pl-wrap">
        <div className="pl-controls">
          <div className="ad-orders" role="radiogroup" aria-label="Stijl">
            {PRESETS.map((x) => (
              <button key={x.id} className="ad-order" role="radio" aria-checked={same(x.v)} onClick={() => void savePrefs(x.v)}>
                <strong>{x.label}</strong>
                <span className="subtle">{x.hint}</span>
              </button>
            ))}
            {custom && (
              <span className="ad-order" role="radio" aria-checked="true">
                <strong>Eigen</strong>
                <span className="subtle">Zelf aangepast</span>
              </span>
            )}
          </div>
          <div className="ua2-selects">
            <label className="ua2-select">Wat
              <select value={p.posterLabels} onChange={(e) => void savePrefs({ posterLabels: e.target.value as P['posterLabels'] })}>
                <option value="skin">Standaard</option>
                <option value="off">Geen</option>
                <option value="basic">Beeld</option>
                <option value="full">Beeld en geluid</option>
              </select>
            </label>
            <label className="ua2-select">Plek
              <select value={p.posterLabelsPos} onChange={(e) => void savePrefs({ posterLabelsPos: e.target.value as P['posterLabelsPos'] })}>
                <option value="skin">Standaard</option>
                <option value="tl">Linksboven</option>
                <option value="tr">Rechtsboven</option>
                <option value="bl">Linksonder</option>
                <option value="br">Rechtsonder</option>
              </select>
            </label>
            <label className="ua2-select">Labels
              <select value={p.posterLabelStack} onChange={(e) => void savePrefs({ posterLabelStack: e.target.value as Prefs['posterLabelStack'] })}>
                <option value="auto">Automatisch (onder elkaar op posters)</option>
                <option value="row">Naast elkaar</option>
                <option value="column">Onder elkaar</option>
              </select>
            </label>
            <label className="ua2-select">Plek van de score
              <select value={p.posterScorePos} disabled={!p.posterScore} onChange={(e) => void savePrefs({ posterScorePos: e.target.value as Prefs['posterScorePos'] })}>
                <option value="auto">Automatisch (vrije hoek)</option>
                <option value="tl">Linksboven</option>
                <option value="bl">Linksonder</option>
                <option value="br">Rechtsonder</option>
              </select>
            </label>
            <label className="ua2-select">Grootte
              <select value={p.posterLabelSize} onChange={(e) => void savePrefs({ posterLabelSize: e.target.value as P['posterLabelSize'] })}>
                <option value="small">Klein</option>
                <option value="normal">Normaal</option>
                <option value="large">Groot</option>
              </select>
            </label>
          </div>
          <div className="ua2-row">
            <span className="ua2-row-text">
              <label htmlFor="pl-score">Score tonen</label>
              <span className="subtle">De score uit Plex (publiek of critici), bijv. ★ 8,1</span>
            </span>
            <input id="pl-score" className="sw" type="checkbox" role="switch" checked={p.posterScore} onChange={(e) => void savePrefs({ posterScore: e.target.checked })} />
          </div>
        </div>
        <figure className="pl-preview" aria-label="Voorbeeld">
          <div className="card card-poster">
            <div className="card-art pl-art">
              <TileFlags flags={SAMPLE} />
              <span className="lib-badge own"><span className="lb-icon" aria-hidden="true">✓</span><span className="lb-text">✓ In bibliotheek</span></span>
              <span className="tile-score" aria-hidden="true">★ 8,1</span>
            </div>
            <span className="card-title">Voorbeeldtitel</span>
          </div>
          <figcaption className="subtle">Voorbeeld</figcaption>
        </figure>
      </div>
    </section>
  );
}
